import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { catchError, distinctUntilChanged, map } from 'rxjs/operators';
import { FormsUtils } from '../../shared/forms.utils';
import { DependentFieldFilters, Field } from '../../shared/interfaces/hubspot-form-data';
import { HubspotOperators } from '../../shared/services/hubspot/hubspot-operators';
import { FormUtilsService } from '../../shared/services/form-utils.service';
import { ValidatorUtils } from '../../shared/services/validator-utils';
import { FormsService } from '../../shared/services/forms.service';

@Component({
    selector: 'da-form-field-input',
    templateUrl: './input.component.html',
    styleUrls: ['./input.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class InputComponent implements OnInit, OnDestroy {
    public field: Field;
    public group: UntypedFormGroup;
    public fieldType: any;
    public hasFocus = false;
    public formId: string;
    private _$isSubmitting: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private _inititalDependentFields: string;
    private _serviceSubscription: Subscription;

    constructor(
        private _hubspotOperators: HubspotOperators,
        private _formUtils: FormUtilsService,
        private _FormsService: FormsService,
        protected _ref: ChangeDetectorRef,
        private _http: HttpClient
    ) {}

    public get showErrors$(): Observable<boolean> {
        const _control = this.group.get(this.field.name);
        const _showErrors = _control.touched || this._$isSubmitting.getValue();
        return of(_showErrors);
    }

    public ngOnInit() {
        this._inititalDependentFields = JSON.stringify(this.field.dependentFieldFilters);

        this.fieldType = this._getFieldType(this.field.name.toLowerCase());
        this._serviceSubscription = this._FormsService
            .submitting$(this.formId)
            .pipe(distinctUntilChanged())
            .subscribe((isSubmitting) => {
                if (isSubmitting) {
                    this.group.get(this.field.name).markAsTouched();
                    this._markDependentFieldsAsTouched();
                    this._ref.detectChanges();
                }
                this._$isSubmitting.next(isSubmitting);
            });
    }

    ngOnDestroy() {
        this._serviceSubscription.unsubscribe();
    }
    public hasValue(fieldname: string) {
        const _input = this.group.get(fieldname);
        return _input.value ? FormsUtils.trim(_input.value.toString()) !== '' : false;
    }

    public getErrorTranslation(error: any): string {
        return this._formUtils.getErrorTranslation(error, this.group, this.field);
    }

    public getVisibilityByDependentFieldFiltersAndSetValidators(
        dependentField: DependentFieldFilters,
        parentField: AbstractControl
    ): boolean {
        const _isVisible = this._hubspotOperators.shouldBeVisible(dependentField, parentField, this.field);
        const _currentDependentField = this.group.get(dependentField.dependentFormField.name);
        const _currentDependentFieldIsRequiredWhenVisible = this._formUtils.isFieldInitiallyRequired(
            dependentField,
            this._inititalDependentFields
        );
        _currentDependentField.setValidators(
            ValidatorUtils.setValidatorsAgain(dependentField, _isVisible, _currentDependentFieldIsRequiredWhenVisible)
        );
        _currentDependentField.updateValueAndValidity();
        return _isVisible;
    }

    private _markDependentFieldsAsTouched() {
        this.field.dependentFieldFilters.forEach((filter) => this.group.get(filter.dependentFormField.name).markAsTouched());
    }

    private _getFieldType(fieldName: string): string {
        if (fieldName === 'email') {
            return 'email';
        }
        return 'text';
    }

    public validateField() {
        this.group.updateValueAndValidity();
        this._ref.detectChanges();
    }
}
